import type { PublicLanguage } from "./i18n";

export type TrialInvitationCopy = {
  opening: string;
  invalidLink: string;
  unavailable: string;
  unavailableTitle: string;
  eyebrow: string;
  title: string;
  noPayment: string;
  oneUse: string;
  activateBy: string;
  trialBegins: string;
  enterCode: string;
  verifyEmail: string;
  email: string;
  continue: string;
  tryPasskeyAgain: string;
  passkeyHint: string;
  emailCode: string;
  sendCodeError: string;
  sentCode: string;
  verificationCode: string;
  verifyContinue: string;
  differentEmail: string;
  codeError: string;
  completeTitle: string;
  verifiedAs: string;
  legalName: string;
  phone: string;
  license: string;
  terms: string;
  activate: string;
  activationError: string;
};

export const trialInvitationCopy: Record<PublicLanguage, TrialInvitationCopy> = {
  en: {
    opening: "Opening your trial invitation…",
    invalidLink: "This trial invitation link is invalid.",
    unavailable: "This link is invalid, expired, or has already been used.",
    unavailableTitle: "Trial invitation unavailable",
    eyebrow: "Private trial invitation",
    title: "Try {course} for {days} days.",
    noPayment: "No payment is required",
    oneUse: "This link can be used once",
    activateBy: "Activate this invitation by {date}.",
    trialBegins: "Your trial begins when enrollment is completed.",
    enterCode: "Enter your verification code",
    verifyEmail: "Verify your email",
    email: "Email address",
    continue: "Continue",
    tryPasskeyAgain: "Try passkey again",
    passkeyHint: "Use a passkey on this device or scan the browser’s QR code with a nearby phone or tablet.",
    emailCode: "Email me a one-time code",
    sendCodeError: "We could not send the verification code.",
    sentCode: "We sent a six-digit code to {email}.",
    verificationCode: "Verification code",
    verifyContinue: "Verify and continue",
    differentEmail: "Use a different email",
    codeError: "That verification code could not be confirmed.",
    completeTitle: "Complete your trial enrollment",
    verifiedAs: "Verified as {email}",
    legalName: "Legal name",
    phone: "Phone number",
    license: "Professional license number",
    terms: "I understand that access ends after {days} days, no payment will be collected, progress may be retained, and the trial does not provide an official completion certificate or continuing-education credit.",
    activate: "Activate trial",
    activationError: "The trial could not be activated.",
  },
  zh: {
    opening: "正在打开您的试用邀请…",
    invalidLink: "此试用邀请链接无效。",
    unavailable: "此链接无效、已过期或已被使用。",
    unavailableTitle: "试用邀请不可用",
    eyebrow: "专属试用邀请",
    title: "免费试用 {course} {days} 天。",
    noPayment: "无需付款",
    oneUse: "此链接只能使用一次",
    activateBy: "请在 {date} 前激活此邀请。",
    trialBegins: "完成注册后，试用期即开始。",
    enterCode: "输入验证码",
    verifyEmail: "验证您的电子邮件",
    email: "电子邮件地址",
    continue: "继续",
    tryPasskeyAgain: "再次尝试通行密钥",
    passkeyHint: "使用此设备上的通行密钥，或用附近的手机或平板电脑扫描浏览器显示的二维码。",
    emailCode: "通过电子邮件发送一次性验证码",
    sendCodeError: "无法发送验证码。",
    sentCode: "我们已向 {email} 发送六位数验证码。",
    verificationCode: "验证码",
    verifyContinue: "验证并继续",
    differentEmail: "使用其他电子邮件地址",
    codeError: "无法确认该验证码。",
    completeTitle: "完成试用注册",
    verifiedAs: "已验证身份：{email}",
    legalName: "法定姓名",
    phone: "电话号码",
    license: "专业执照号码",
    terms: "我了解，访问权限将在 {days} 天后结束，不会收取任何费用，学习进度可能会被保留，并且本试用不提供正式结业证书或继续教育学分。",
    activate: "激活试用",
    activationError: "无法激活试用。",
  },
  th: {
    opening: "กำลังเปิดคำเชิญทดลองใช้ของคุณ…",
    invalidLink: "ลิงก์คำเชิญทดลองใช้นี้ไม่ถูกต้อง",
    unavailable: "ลิงก์นี้ไม่ถูกต้อง หมดอายุ หรือถูกใช้ไปแล้ว",
    unavailableTitle: "คำเชิญทดลองใช้ไม่พร้อมใช้งาน",
    eyebrow: "คำเชิญทดลองใช้ส่วนตัว",
    title: "ทดลองใช้ {course} เป็นเวลา {days} วัน",
    noPayment: "ไม่ต้องชำระเงิน",
    oneUse: "ลิงก์นี้ใช้ได้หนึ่งครั้ง",
    activateBy: "เปิดใช้คำเชิญนี้ภายใน {date}",
    trialBegins: "ช่วงทดลองใช้จะเริ่มเมื่อการลงทะเบียนเสร็จสมบูรณ์",
    enterCode: "ป้อนรหัสยืนยันของคุณ",
    verifyEmail: "ยืนยันอีเมลของคุณ",
    email: "ที่อยู่อีเมล",
    continue: "ดำเนินการต่อ",
    tryPasskeyAgain: "ลองใช้พาสคีย์อีกครั้ง",
    passkeyHint: "ใช้พาสคีย์บนอุปกรณ์นี้ หรือใช้โทรศัพท์หรือแท็บเล็ตที่อยู่ใกล้สแกนคิวอาร์โค้ดที่เบราว์เซอร์แสดง",
    emailCode: "ส่งรหัสใช้ครั้งเดียวทางอีเมล",
    sendCodeError: "ไม่สามารถส่งรหัสยืนยันได้",
    sentCode: "เราส่งรหัสหกหลักไปที่ {email} แล้ว",
    verificationCode: "รหัสยืนยัน",
    verifyContinue: "ยืนยันและดำเนินการต่อ",
    differentEmail: "ใช้อีเมลอื่น",
    codeError: "ไม่สามารถยืนยันรหัสนั้นได้",
    completeTitle: "ลงทะเบียนทดลองใช้ให้เสร็จสมบูรณ์",
    verifiedAs: "ยืนยันแล้วในชื่อ {email}",
    legalName: "ชื่อตามกฎหมาย",
    phone: "หมายเลขโทรศัพท์",
    license: "หมายเลขใบอนุญาตวิชาชีพ",
    terms: "ฉันเข้าใจว่าสิทธิ์เข้าถึงจะสิ้นสุดหลังจาก {days} วัน จะไม่มีการเรียกเก็บเงิน ความคืบหน้าอาจถูกเก็บไว้ และการทดลองใช้นี้ไม่ออกใบรับรองการจบหลักสูตรอย่างเป็นทางการหรือหน่วยกิตการศึกษาต่อเนื่อง",
    activate: "เปิดใช้การทดลอง",
    activationError: "ไม่สามารถเปิดใช้การทดลองได้",
  },
  my: {
    opening: "သင့်အစမ်းအသုံးပြုရန် ဖိတ်ကြားချက်ကို ဖွင့်နေသည်…",
    invalidLink: "ဤအစမ်းအသုံးပြုရန် ဖိတ်ကြားလင့်ခ် မမှန်ကန်ပါ။",
    unavailable: "ဤလင့်ခ်သည် မမှန်ကန်ပါ၊ သက်တမ်းကုန်သွားပါပြီ သို့မဟုတ် အသုံးပြုပြီးသားဖြစ်သည်။",
    unavailableTitle: "အစမ်းအသုံးပြုရန် ဖိတ်ကြားချက် မရနိုင်ပါ",
    eyebrow: "ကိုယ်ပိုင်အစမ်းအသုံးပြုရန် ဖိတ်ကြားချက်",
    title: "{course} ကို {days} ရက် အစမ်းအသုံးပြုပါ။",
    noPayment: "ငွေပေးချေရန် မလိုပါ",
    oneUse: "ဤလင့်ခ်ကို တစ်ကြိမ်သာ သုံးနိုင်သည်",
    activateBy: "ဤဖိတ်ကြားချက်ကို {date} မတိုင်မီ အသက်သွင်းပါ။",
    trialBegins: "သင်တန်းစာရင်းသွင်းမှု ပြီးဆုံးသည့်အခါ အစမ်းကာလ စတင်ပါမည်။",
    enterCode: "အတည်ပြုကုဒ်ကို ထည့်ပါ",
    verifyEmail: "သင့်အီးမေးလ်ကို အတည်ပြုပါ",
    email: "အီးမေးလ်လိပ်စာ",
    continue: "ဆက်လုပ်ရန်",
    tryPasskeyAgain: "Passkey ကို ထပ်စမ်းရန်",
    passkeyHint: "ဤစက်ရှိ passkey ကို သုံးပါ သို့မဟုတ် အနီးရှိဖုန်း သို့မဟုတ် တက်ဘလက်ဖြင့် ဘရောက်ဇာ၏ QR ကုဒ်ကို စကင်ဖတ်ပါ။",
    emailCode: "တစ်ကြိမ်သုံးကုဒ်ကို အီးမေးလ်ပို့ရန်",
    sendCodeError: "အတည်ပြုကုဒ်ကို မပို့နိုင်ပါ။",
    sentCode: "ဂဏန်းခြောက်လုံးပါကုဒ်ကို {email} သို့ ပို့ထားပါသည်။",
    verificationCode: "အတည်ပြုကုဒ်",
    verifyContinue: "အတည်ပြုပြီး ဆက်လုပ်ရန်",
    differentEmail: "အခြားအီးမေးလ် သုံးရန်",
    codeError: "ထိုအတည်ပြုကုဒ်ကို အတည်မပြုနိုင်ပါ။",
    completeTitle: "အစမ်းသင်တန်းစာရင်းသွင်းမှုကို ပြီးစီးပါ",
    verifiedAs: "{email} အဖြစ် အတည်ပြုပြီး",
    legalName: "တရားဝင်အမည်",
    phone: "ဖုန်းနံပါတ်",
    license: "အသက်မွေးဝမ်းကျောင်းလိုင်စင်နံပါတ်",
    terms: "ဝင်ရောက်အသုံးပြုခွင့်သည် {days} ရက်အကြာတွင် ကုန်ဆုံးမည်၊ ငွေကောက်ခံမည်မဟုတ်၊ လေ့လာမှုတိုးတက်မှုကို ဆက်လက်သိမ်းထားနိုင်ပြီး ဤအစမ်းအသုံးပြုမှုမှ တရားဝင်သင်တန်းဆင်းလက်မှတ် သို့မဟုတ် ဆက်လက်ပညာရေးခရက်ဒစ် မရရှိကြောင်း နားလည်ပါသည်။",
    activate: "အစမ်းအသုံးပြုမှုကို အသက်သွင်းရန်",
    activationError: "အစမ်းအသုံးပြုမှုကို အသက်မသွင်းနိုင်ပါ။",
  },
  vi: {
    opening: "Đang mở lời mời dùng thử của bạn…",
    invalidLink: "Liên kết mời dùng thử này không hợp lệ.",
    unavailable: "Liên kết này không hợp lệ, đã hết hạn hoặc đã được sử dụng.",
    unavailableTitle: "Lời mời dùng thử không khả dụng",
    eyebrow: "Lời mời dùng thử riêng",
    title: "Dùng thử {course} trong {days} ngày.",
    noPayment: "Không cần thanh toán",
    oneUse: "Liên kết này chỉ dùng được một lần",
    activateBy: "Hãy kích hoạt lời mời này trước {date}.",
    trialBegins: "Thời gian dùng thử bắt đầu khi hoàn tất ghi danh.",
    enterCode: "Nhập mã xác minh",
    verifyEmail: "Xác minh email của bạn",
    email: "Địa chỉ email",
    continue: "Tiếp tục",
    tryPasskeyAgain: "Thử lại khóa truy cập",
    passkeyHint: "Dùng khóa truy cập trên thiết bị này hoặc dùng điện thoại hay máy tính bảng ở gần quét mã QR của trình duyệt.",
    emailCode: "Gửi mã dùng một lần qua email",
    sendCodeError: "Không thể gửi mã xác minh.",
    sentCode: "Chúng tôi đã gửi mã gồm sáu chữ số đến {email}.",
    verificationCode: "Mã xác minh",
    verifyContinue: "Xác minh và tiếp tục",
    differentEmail: "Dùng email khác",
    codeError: "Không thể xác nhận mã xác minh đó.",
    completeTitle: "Hoàn tất ghi danh dùng thử",
    verifiedAs: "Đã xác minh là {email}",
    legalName: "Họ tên pháp lý",
    phone: "Số điện thoại",
    license: "Số giấy phép hành nghề",
    terms: "Tôi hiểu rằng quyền truy cập sẽ kết thúc sau {days} ngày, không có khoản thanh toán nào được thu, tiến độ có thể được lưu lại và bản dùng thử không cấp chứng nhận hoàn thành chính thức hoặc tín chỉ giáo dục thường xuyên.",
    activate: "Kích hoạt dùng thử",
    activationError: "Không thể kích hoạt bản dùng thử.",
  },
  es: {
    opening: "Abriendo tu invitación de prueba…",
    invalidLink: "Este enlace de invitación de prueba no es válido.",
    unavailable: "Este enlace no es válido, caducó o ya fue utilizado.",
    unavailableTitle: "Invitación de prueba no disponible",
    eyebrow: "Invitación privada de prueba",
    title: "Prueba {course} durante {days} días.",
    noPayment: "No se requiere pago",
    oneUse: "Este enlace solo puede usarse una vez",
    activateBy: "Activa esta invitación antes del {date}.",
    trialBegins: "La prueba comienza cuando se completa la inscripción.",
    enterCode: "Ingresa tu código de verificación",
    verifyEmail: "Verifica tu correo electrónico",
    email: "Correo electrónico",
    continue: "Continuar",
    tryPasskeyAgain: "Volver a intentar con llave de acceso",
    passkeyHint: "Usa una llave de acceso en este dispositivo o escanea el código QR del navegador con un teléfono o una tableta cercanos.",
    emailCode: "Enviarme un código de un solo uso",
    sendCodeError: "No pudimos enviar el código de verificación.",
    sentCode: "Enviamos un código de seis dígitos a {email}.",
    verificationCode: "Código de verificación",
    verifyContinue: "Verificar y continuar",
    differentEmail: "Usar otro correo electrónico",
    codeError: "No se pudo confirmar ese código de verificación.",
    completeTitle: "Completa tu inscripción de prueba",
    verifiedAs: "Verificado como {email}",
    legalName: "Nombre legal",
    phone: "Número de teléfono",
    license: "Número de licencia profesional",
    terms: "Entiendo que el acceso termina después de {days} días, no se cobrará ningún pago, el progreso puede conservarse y la prueba no proporciona un certificado oficial de finalización ni créditos de educación continua.",
    activate: "Activar prueba",
    activationError: "No se pudo activar la prueba.",
  },
};

export function fillTrialCopy(template: string, values: Record<string, string | number>) {
  return Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, String(value)), template);
}
